import { useState } from "react";
import { Navigate, useParams } from "react-router";
import { fmtDateTime } from "@/components/admin/format";
import { IssueNote } from "@/components/admin/IssueNote";
import { JsonEditor } from "@/components/admin/JsonEditor";
import { KIND_META, isMasterKind, keyOf, type MasterKind } from "@/components/admin/master-kinds";
import { FactorForm, SourceForm, TechnologyForm } from "@/components/admin/MasterForms";
import { LoadState, PageHead } from "@/components/admin/PageHead";
import { useLoad } from "@/components/admin/useLoad";
import { errorMessage, toIssues, type FieldIssue } from "@/components/auth/issues";
import { Button } from "@/components/ui/FormControls";
import { InfoTip } from "@/components/ui/InfoTip";
import { ApiError, api } from "@/lib/api";
import { useMaster } from "@/store/master";

interface DbRow {
  key: string;
  data: unknown;
  active: boolean;
  version: number;
  updatedAt: string;
  updatedBy: string | null;
}
interface DefaultRow {
  key: string;
  data: unknown;
  inDb: boolean;
}
interface KindResponse {
  kind: MasterKind;
  label: string;
  rows: DbRow[];
  defaults: DefaultRow[];
}

interface MasterVersion {
  id: string;
  version: number;
  active: boolean;
  action: string;
  data: unknown;
  changedBy: string | null;
  createdAt: string;
}

/** 편집 중인 항목 , key 가 있으면 그 키로 저장(키 고정), 없으면 새 항목 */
interface Editing {
  key: string | null;
  initial: Record<string, unknown>;
}

export function MasterPage() {
  const { kind } = useParams();
  if (!kind || !isMasterKind(kind)) return <Navigate to="/admin/master/factor" replace />;
  return <MasterKindPage key={kind} kind={kind} />;
}

/**
 * 한 종류의 마스터 데이터 , DB 항목과, 아직 DB 에 없는 내장 기본값.
 * 저장(PUT)은 브라우저에서 zod 로 먼저 거르고, 서버 오류(issues)도 같은 자리에 보인다.
 */
function MasterKindPage({ kind }: { kind: MasterKind }) {
  const meta = KIND_META[kind];
  const { data, error, loading, reload } = useLoad<KindResponse>(`/admin/master/${kind}`);
  const loadMaster = useMaster((s) => s.load);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [fail, setFail] = useState<string | null>(null);
  const [issues, setIssues] = useState<FieldIssue[]>([]);
  const [historyKey, setHistoryKey] = useState<string | null>(null);
  const [history, setHistory] = useState<MasterVersion[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  function clearNotes() {
    setOk(null);
    setFail(null);
    setIssues([]);
  }

  /** 스키마를 통과한 객체를 그 키로 PUT. 성공하면 목록과 참고 데이터를 다시 읽는다 */
  async function put(obj: Record<string, unknown>, label: string): Promise<boolean> {
    clearNotes();
    const parsed = meta.schema.safeParse(obj);
    if (!parsed.success) {
      setIssues([...parsed.error.issues]);
      return false;
    }
    const key = keyOf(kind, parsed.data);
    if (!key) {
      setIssues([{ path: [meta.key], message: "키를 입력해 주세요" }]);
      return false;
    }
    setBusy(key);
    try {
      const r = await api<{ key: string; version: number }>(`/admin/master/${kind}/${encodeURIComponent(key)}`, { method: "PUT", json: parsed.data });
      setOk(`${label}: ${r.key} 저장 (v${r.version})`);
      reload();
      void loadMaster();
      return true;
    } catch (e) {
      setFail(errorMessage(e));
      if (e instanceof ApiError) setIssues(toIssues(e.issues));
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function deactivate(row: DbRow) {
    if (!window.confirm(`${row.key} 을(를) 비활성화합니다. 산정 화면과 참고 데이터에서 사라지고, 예시나 설비가 참조하면 그쪽에서 오류가 날 수 있습니다.`)) return;
    clearNotes();
    setBusy(row.key);
    try {
      await api(`/admin/master/${kind}/${encodeURIComponent(row.key)}`, { method: "DELETE" });
      setOk(`${row.key} 비활성화`);
      reload();
      void loadMaster();
    } catch (e) {
      setFail(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  function openEditor(next: Editing) {
    clearNotes();
    setEditing(next);
    window.scrollTo({ top: 0 });
  }

  async function openHistory(key: string) {
    setHistoryKey(key);
    setHistoryLoading(true);
    try {
      const response = await api<{ versions: MasterVersion[] }>(`/admin/master/${kind}/${encodeURIComponent(key)}/history`);
      setHistory(response.versions);
    } catch (e) {
      setFail(errorMessage(e));
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  async function rollback(version: MasterVersion) {
    if (!historyKey || !window.confirm(`${historyKey} 항목을 v${version.version}으로 되돌릴까요? 새 버전으로 기록됩니다.`)) return;
    try {
      await api(`/admin/master/${kind}/${encodeURIComponent(historyKey)}/rollback`, { method: "POST", json: { version: version.version } });
      setOk(`${historyKey}을(를) v${version.version} 기준으로 복구했습니다.`);
      await openHistory(historyKey);
      reload();
      void loadMaster();
    } catch (e) {
      setFail(errorMessage(e));
    }
  }

  const rows = data?.rows ?? [];
  const pending = (data?.defaults ?? []).filter((d) => !d.inDb);
  const activeCount = rows.filter((r) => r.active).length;

  return (
    <div className="admin-page">
      <PageHead
        title={meta.label}
        desc={
          <>
            {meta.hint}{" "}
            <InfoTip label="출처 규칙">DB에 이 종류의 항목이 하나라도 활성 상태로 있으면 프로그램은 DB 값을 사용합니다. 내장 기본값 중 DB에 없는 항목은 사용자 화면에 표시되지 않으므로 필요한 항목은 아래에서 가져오세요.</InfoTip>
          </>
        }
        aside={
          <Button variant="primary" disabled={!!editing} onClick={() => openEditor({ key: null, initial: meta.blank() })}>
            새 항목
          </Button>
        }
      />

      {ok && (
        <p role="status" className="note note-ok text-label">
          {ok}
        </p>
      )}
      {fail && (
        <p role="alert" className="note note-danger text-label">
          {fail}
        </p>
      )}
      <IssueNote title="스키마 검사에 걸렸습니다" issues={issues} labels={meta.labels} />

      {editing && (
        <section className="admin-card" aria-label="편집">
          <div><h2 className="admin-card-title">{editing.key ? `편집: ${editing.key}` : `새 ${meta.label}`}</h2>
          <p className="admin-card-desc">{editing.key ? "저장하면 버전이 하나 올라가고 변경 이력에 남습니다." : `${meta.key} 가 키가 됩니다. 저장 뒤에는 바꿀 수 없습니다.`}</p></div>
          <div>
            <Editor
              kind={kind}
              initial={editing.initial}
              lockKey={editing.key !== null}
              busy={busy !== null}
              onCancel={() => setEditing(null)}
              onSubmit={(obj) => {
                void put(obj, editing.key ? "수정" : "추가").then((done) => {
                  if (done) setEditing(null);
                });
              }}
            />
          </div>
        </section>
      )}

      <LoadState loading={loading && !data} error={error} />

      {data && (
        <>
          <section>
            <h2 className="admin-card-title">
              DB 항목 <span className="font-mono text-caption font-semibold text-ink-muted">{activeCount}/{rows.length}</span>
            </h2>
            {rows.length === 0 ? (
              <p className="mt-item text-caption text-ink-subtle">DB 에 항목이 없어 프로그램은 내장 기본값을 씁니다.</p>
            ) : (
              <div className="table-scroll admin-table-wrap mt-item">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>키</th>
                      {meta.columns.map((c) => (
                        <th key={c}>{c}</th>
                      ))}
                      <th>상태</th>
                      <th className="cell-num">버전</th>
                      <th>수정</th>
                      <th>수정자</th>
                      <th>동작</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.key} data-off={r.active ? undefined : "true"}>
                        <td className="cell-key">{r.key}</td>
                        <Cells kind={kind} data={r.data} />
                        <td className="cell-nowrap">{r.active ? <span className="badge-ok">사용</span> : <span className="badge-muted">비활성</span>}</td>
                        <td className="cell-num">v{r.version}</td>
                        <td className="cell-nowrap cell-muted">{fmtDateTime(r.updatedAt)}</td>
                        <td className="cell-muted">{r.updatedBy ?? "없음"}</td>
                        <td>
                          <div className="row-actions">
                            <Button size="compact" disabled={busy !== null} onClick={() => openEditor({ key: r.key, initial: r.data as Record<string, unknown> })}>
                              편집
                            </Button>
                            <Button size="compact" onClick={() => void openHistory(r.key)}>이력</Button>
                            {r.active ? (
                              <Button variant="danger" size="compact" disabled={busy !== null} onClick={() => void deactivate(r)}>
                                비활성화
                              </Button>
                            ) : (
                              <Button size="compact" disabled={busy !== null} onClick={() => void put(r.data as Record<string, unknown>, "다시 사용")}>
                                다시 사용
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h2 className="admin-card-title">
              내장 기본값, DB에 없음 <span className="font-mono text-caption font-semibold text-ink-muted">{pending.length}</span>
            </h2>
            {pending.length === 0 ? (
              <p className="mt-item text-caption text-ink-subtle">내장 기본값이 모두 DB 에 있습니다.</p>
            ) : (
              <div className="table-scroll admin-table-wrap mt-item">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>키</th>
                      {meta.columns.map((c) => (
                        <th key={c}>{c}</th>
                      ))}
                      <th>동작</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.map((d) => (
                      <tr key={d.key}>
                        <td className="cell-key">{d.key}</td>
                        <Cells kind={kind} data={d.data} />
                        <td>
                          <div className="row-actions">
                            <Button size="compact" disabled={busy !== null} onClick={() => void put(d.data as Record<string, unknown>, "가져오기")}>
                              {busy === d.key ? "가져오는 중…" : "DB 로 가져오기"}
                            </Button>
                            <Button size="compact" disabled={busy !== null} onClick={() => openEditor({ key: d.key, initial: d.data as Record<string, unknown> })}>
                              고쳐서 가져오기
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {historyKey && (
            <section className="admin-card">
              <div className="admin-card-head"><div><h2 className="admin-card-title">{historyKey} 변경 이력</h2><p className="admin-card-desc">현재 값을 직접 덮어쓰지 않고 새 버전으로 복구합니다.</p></div><Button onClick={() => setHistoryKey(null)}>닫기</Button></div>
              {historyLoading ? <p className="text-caption text-ink-subtle">이력을 불러오는 중입니다.</p> : history.length === 0 ? <p className="text-caption text-ink-subtle">아직 저장된 버전이 없습니다. 다음 변경부터 기록됩니다.</p> : <div className="table-scroll admin-table-wrap"><table className="admin-table"><thead><tr><th>버전</th><th>동작</th><th>상태</th><th>변경자</th><th>시각</th><th>동작</th></tr></thead><tbody>{history.map((version) => <tr key={version.id}><td className="cell-num">v{version.version}</td><td>{version.action}</td><td>{version.active ? <span className="badge-ok">사용</span> : <span className="badge-muted">비활성</span>}</td><td>{version.changedBy ?? "없음"}</td><td className="cell-nowrap">{fmtDateTime(version.createdAt)}</td><td><Button size="compact" onClick={() => void rollback(version)}>이 버전으로 복구</Button></td></tr>)}</tbody></table></div>}
            </section>
          )}
        </>
      )}
    </div>
  );
}

function Cells({ kind, data }: { kind: MasterKind; data: unknown }) {
  let cells: { text: string; mono?: boolean }[];
  try {
    cells = KIND_META[kind].cells(data);
  } catch {
    cells = KIND_META[kind].columns.map(() => ({ text: "?" }));
  }
  return (
    <>
      {cells.map((c, i) => (
        <td key={i} className={c.mono ? "cell-nowrap font-mono" : undefined}>
          {c.text || "없음"}
        </td>
      ))}
    </>
  );
}

function Editor({ kind, initial, lockKey, busy, onSubmit, onCancel }: { kind: MasterKind; initial: Record<string, unknown>; lockKey: boolean; busy: boolean; onSubmit: (obj: Record<string, unknown>) => void; onCancel: () => void }) {
  const props = { initial, lockKey, busy, onSubmit, onCancel };
  switch (kind) {
    case "factor":
      return <FactorForm {...props} />;
    case "source":
      return <SourceForm {...props} />;
    case "technology":
      return <TechnologyForm {...props} />;
    default:
      return <JsonEditor {...props} keyField={KIND_META[kind].key} />;
  }
}
